import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { checkoutCart, money, verificationStatus } from "../lib/api";
import VerificationModal from "./VerificationModal";

export default function CartDrawer() {
  const { items, setQty, removeItem, subtotal, open, setOpen } = useCart();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const MIN_ORDER = 60;
  const belowMin = subtotal < MIN_ORDER;
  const remaining = Math.max(0, Math.round((MIN_ORDER - subtotal) * 100) / 100);

  const checkout = async () => {
    if (belowMin) return;
    const savedEmail = localStorage.getItem("fd_verify_email");
    const savedName = localStorage.getItem("fd_verify_name");
    if (!savedEmail) {
      setVerifyOpen(true);
      return;
    }
    setCheckingOut(true);
    try {
      const st = await verificationStatus(savedEmail).catch(() => null);
      if (st && !st.verified) {
        localStorage.removeItem("fd_verify_email");
        localStorage.removeItem("fd_verify_name");
        setCheckingOut(false);
        setVerifyOpen(true);
        return;
      }
      const res = await checkoutCart(
        items.map((i) => ({ product_id: i.product_id, qty: i.qty, option: i.option })),
        { full_name: savedName || "Returning Customer", email: savedEmail }
      );
      if (res.status === "ready") {
        window.location.href = "/checkout";
      } else {
        setVerifyOpen(true);
      }
    } catch {
      setVerifyOpen(true);
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="border-b border-neutral-200 p-5">
          <SheetTitle className="font-display text-xl font-black tracking-tight">
            Your Cart {items.length > 0 && <span className="text-neutral-400">({items.length})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center" data-testid="cart-empty-state">
            <ShoppingBag className="h-10 w-10 text-neutral-300" />
            <p className="text-sm text-neutral-500">Your cart is empty. Let's fix that.</p>
            <button
              onClick={() => setOpen(false)}
              className="rounded-full bg-brand-ink px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-neutral-800"
              data-testid="cart-continue-shopping"
            >
              CONTINUE SHOPPING
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5">
              <ul className="space-y-5">
                {items.map((item) => (
                  <li key={item.key} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                    <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="h-20 w-20 flex-shrink-0 overflow-hidden bg-brand-sand">
                      {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" />}
                    </Link>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <Link to={`/product/${item.slug}`} onClick={() => setOpen(false)} className="truncate text-sm font-semibold hover:text-brand-red">
                        {item.name}
                      </Link>
                      {item.option && <p className="text-xs text-neutral-500">{item.option}</p>}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="flex items-center border border-neutral-200">
                          <button className="p-1.5 transition-colors hover:bg-neutral-100" onClick={() => setQty(item.key, item.qty - 1)} aria-label="Decrease quantity" data-testid="cart-qty-minus">
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold" data-testid="cart-item-qty">{item.qty}</span>
                          <button className="p-1.5 transition-colors hover:bg-neutral-100" onClick={() => setQty(item.key, item.qty + 1)} aria-label="Increase quantity" data-testid="cart-qty-plus">
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <p className="text-sm font-bold">{money(item.unit_price * item.qty)}</p>
                      </div>
                    </div>
                    <button onClick={() => removeItem(item.key)} aria-label="Remove item" className="self-start p-1 text-neutral-400 transition-colors hover:text-brand-red" data-testid="cart-remove-item">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-neutral-200 p-5">
              <div className="mb-3 flex items-center gap-2 bg-brand-sand p-3 text-xs font-semibold text-neutral-700">
                <Truck className="h-4 w-4 text-brand-ink" />
                Fast, discreet delivery across DC — typically 30–90 minutes.
              </div>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-neutral-500">Subtotal</span>
                <span className="font-display text-xl font-black" data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              {belowMin ? (
                <div className="mb-4" data-testid="min-order-notice">
                  <div className="flex items-center justify-between text-xs font-bold">
                    <span className="text-brand-red">Add {money(remaining)} more — ${MIN_ORDER} minimum order</span>
                    <span className="text-neutral-400">{money(subtotal)} / {money(MIN_ORDER)}</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-200">
                    <div
                      className="h-full rounded-full bg-brand-red transition-[width] duration-500"
                      style={{ width: `${Math.min(100, (subtotal / MIN_ORDER) * 100)}%` }}
                      data-testid="min-order-progress"
                    />
                  </div>
                </div>
              ) : (
                <div className="mb-4 flex items-center gap-2 bg-brand-lime/20 p-3 text-xs font-bold text-brand-ink" data-testid="min-order-met">
                  Minimum order met — you're good to go
                </div>
              )}
              <button
                onClick={checkout}
                disabled={checkingOut || belowMin}
                data-testid="cart-checkout-button"
                className="w-full rounded-full bg-brand-lime py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {checkingOut ? "PREPARING CHECKOUT..." : belowMin ? `$${MIN_ORDER} MINIMUM ORDER` : "PROCEED TO CHECKOUT"}
              </button>
              <button onClick={() => setOpen(false)} className="mt-3 w-full text-center text-xs font-semibold text-neutral-500 underline-offset-4 hover:underline" data-testid="cart-keep-shopping">
                Continue shopping
              </button>
              <p className="mt-4 text-center text-[10px] leading-relaxed text-neutral-400">
                21+ only. ID verification required at checkout and delivery. Initiative 71 compliant gifting.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
    <VerificationModal open={verifyOpen} onClose={() => setVerifyOpen(false)} items={items} />
    </>
  );
}